import {
	ARCHIVED_V1_CATEGORY_IDS,
	ATTRIBUTE_SETS,
	CATEGORY_TREE_V1,
	flattenCategoryTreeV2,
} from "@osmix/catalog-core/category-tree";
import { isSizeGridId } from "@osmix/catalog-core/size-grids";
import { buildCategoryTree, MAX_CATEGORY_DEPTH } from "@/lib/category-tree";
import { sleep } from "@/lib/utils";

import type { Api, Category, CategoryAttributeDefinition, CategoryInput } from "../types";
import { productCountsByCategory } from "./products";

// Офлайн-зеркало дерева категорий v2 (миграция 134): активные узлы из
// catalog-core и архивные корни v1, как они лежат на боевой базе.

let nextId = 1;
const mintId = () => `cat_demo_${nextId++}`;

const v2 = flattenCategoryTreeV2();
let fixtures: Category[] = [
	...v2.map((n) => ({
		id: n.id,
		name: n.name,
		parentId: n.parentId,
		sortOrder: n.sortOrder,
		section: n.section,
		gender: n.gender,
		sizeGrid: n.sizeGrid ?? null,
		imageUrl: null,
		archived: false,
		productCount: 0,
		publishedCount: 0,
	})),
	...CATEGORY_TREE_V1.filter((n) => (ARCHIVED_V1_CATEGORY_IDS as readonly string[]).includes(n.id)).map((n, i) => ({
		id: n.id,
		name: n.name,
		parentId: null,
		sortOrder: 10_000 + i,
		section: "other" as const,
		gender: "unisex" as const,
		sizeGrid: null,
		imageUrl: null,
		archived: true,
		productCount: 0,
		publishedCount: 0,
	})),
];

const schemas = new Map<string, CategoryAttributeDefinition[]>(
	v2
		.filter((n) => n.attributes)
		.map((n) => [n.id, ATTRIBUTE_SETS[n.attributes as keyof typeof ATTRIBUTE_SETS] as CategoryAttributeDefinition[]]),
);

/** Узлы как есть (без счётчиков) — для фильтра товаров по ветке. */
export const categoryFixtures = (): Category[] => fixtures;

const checkGrid = (grid: string | null | undefined) => {
	if (grid && !isSizeGridId(grid)) throw new Error(`Неизвестная размерная сетка «${grid}»`);
};

const checkParent = (parentId: string | null | undefined, selfId?: string) => {
	if (!parentId) return;
	const tree = buildCategoryTree(fixtures);
	const parent = tree.byId.get(parentId);
	if (!parent) throw new Error("parentId not found");
	const self = selfId ? tree.byId.get(selfId) : undefined;
	let cur: string | null = parentId;
	while (cur) {
		if (cur === selfId) throw new Error("Нельзя вложить категорию в её же подкатегорию");
		cur = tree.byId.get(cur)?.category.parentId ?? null;
	}
	if (parent.depth + (self?.height ?? 1) > MAX_CATEGORY_DEPTH)
		throw new Error(`Дерево категорий — не глубже ${MAX_CATEGORY_DEPTH} уровней`);
};

export const categories: Api["categories"] = {
	async list() {
		await sleep(120);
		// Счётчики — по демо-карточкам, как шлюз считает по products.
		const counts = productCountsByCategory();
		return [...fixtures]
			.sort((a, b) => a.sortOrder - b.sortOrder)
			.map((c) => ({
				...c,
				productCount: counts.get(c.id)?.products ?? 0,
				publishedCount: counts.get(c.id)?.published ?? 0,
			}));
	},

	async create(input: CategoryInput) {
		await sleep(150);
		checkGrid(input.sizeGrid);
		checkParent(input.parentId);
		const next: Category = {
			id: mintId(),
			name: input.name,
			parentId: input.parentId ?? null,
			sortOrder: input.sortOrder ?? fixtures.reduce((m, c) => Math.max(m, c.sortOrder), 0) + 10,
			sizeGrid: input.sizeGrid ?? null,
			imageUrl: input.imageUrl ?? null,
			archived: false,
		};
		fixtures = [...fixtures, next];
		return next;
	},

	async update(id, patch) {
		await sleep(150);
		const prev = fixtures.find((c) => c.id === id);
		if (!prev) throw new Error("Category not found");
		checkGrid(patch.sizeGrid);
		if (patch.parentId !== undefined && patch.parentId !== prev.parentId) checkParent(patch.parentId, id);
		const next: Category = {
			...prev,
			name: patch.name ?? prev.name,
			parentId: patch.parentId === undefined ? prev.parentId : patch.parentId,
			sortOrder: patch.sortOrder ?? prev.sortOrder,
			sizeGrid: patch.sizeGrid === undefined ? prev.sizeGrid : patch.sizeGrid || null,
			imageUrl: patch.imageUrl === undefined ? prev.imageUrl : patch.imageUrl || null,
			archived: patch.archived ?? prev.archived,
		};
		fixtures = fixtures.map((c) => (c.id === id ? next : c));
		return next;
	},

	async deleteImpact() {
		await sleep(80);
		return { products: 0, commissionRates: 0, buyerRequests: 0 };
	},

	async setFacets(id, input) {
		await sleep(80);
		fixtures = fixtures.map((c) => (c.id === id ? { ...c, ...input } : c));
	},

	async attributeSchema(id) {
		await sleep(60);
		if (!fixtures.some((c) => c.id === id)) throw new Error("Category not found");
		return { categoryId: id, attributes: schemas.get(id) ?? [] };
	},

	async updateAttributeSchema(id, attributes) {
		await sleep(120);
		if (!fixtures.some((c) => c.id === id)) throw new Error("Category not found");
		// Те же отказы, что у шлюза.
		const keys = new Set<string>();
		for (const a of attributes) {
			if (keys.has(a.key)) throw new Error(`Duplicate attribute key '${a.key}'`);
			keys.add(a.key);
			if (a.type === "select" && !a.options?.length) throw new Error(`Select attribute '${a.key}' requires options`);
		}
		schemas.set(id, attributes);
		return { categoryId: id, attributes, updatedAt: new Date().toISOString() };
	},

	async resetAttributeSchema(id) {
		await sleep(80);
		if (!fixtures.some((c) => c.id === id)) throw new Error("Category not found");
		schemas.delete(id);
	},

	async sectionCounts() {
		await sleep(80);
		return [];
	},

	async remove(id) {
		await sleep(150);
		if (fixtures.some((c) => c.parentId === id)) throw new Error("Category has children; remove them first");
		fixtures = fixtures.filter((c) => c.id !== id);
	},
};
